// src/pages/ArtistDashboard.js
import React, { useEffect, useState } from 'react';
import { http } from "../lib/axios";
import {
  Box, Container, Typography, Grid, Slider,
  Tabs, Tab, FormControl, InputLabel, Select, MenuItem,
  Checkbox, ListItemText, OutlinedInput, Chip, Button, Stack,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton,
  Tooltip as MuiTooltip,
} from '@mui/material';
import ArtworkCard from '../components/ArtworkCard';
import ArtistProfileCard from '../components/ArtistProfileCard';
import HostProfileCard from '../components/HostProfileCard';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Tooltip, Circle, Popup, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import PayoutsCard from "../components/PayoutsCard";
import { FavoritesSection } from "../components/FavoritesSection";
import ArtworkCreatePage from './ArtworkCreatePage'; // ← uses your existing page component
import MiniArtistCard from '../components/MiniArtistCard';
import MiniHostCard from '../components/MiniHostCard';
import ArtworkAgreementEditorModal from "../components/ArtworkAgreementEditorModal";
import PendingAgreementsList from "../components/PendingAgreementsList";
import { useArtistHostedArtworks } from "../hooks/useArtistHostedArtworks";
import SalesReportPanel from "../components/SalesReportPanel";
import { approvePaymentRefund, exportArtistSalesCsv, getArtistSales, rejectPaymentRefund } from "../lib/apiPayments";
import { useChat } from '../context/ChatContext';

const redIcon = new L.Icon({
  iconUrl: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32]
});
const blueIcon = new L.Icon({
  iconUrl: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32]
});
const greenIcon = new L.Icon({
  iconUrl: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32]
});

function MapClickHandler({ onClick }) {
  useMapEvents({
    click(e) {
      onClick([e.latlng.lat, e.latlng.lng]);
    }
  });
  return null;
}

function RecenterOnPosition({ position }) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.setView(position);
    }
  }, [map, position]);

  return null;
}

export default function ArtistDashboard() {
  const { launchChat } = useChat();
  const [artist, setArtist] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [agreementModalOpen, setAgreementModalOpen] = useState(false);
  const [agreementModalMode, setAgreementModalMode] = useState("respond");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelAgreement, setCancelAgreement] = useState(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [nearbyHosts, setNearbyHosts] = useState([]);
  const [nearbyArtists, setNearbyArtists] = useState([]);
  const [distance, setDistance] = useState(20);
  const [position, setPosition] = useState(null);
  const ARTWORK_STATUSES = ["available","hosted","approved","sold","featured"];
  const [statusFilter, setStatusFilter] = useState([]); // [] => show all
  const isAuthenticated = !!localStorage.getItem('access_token');
  const handleStartChat = launchChat;

  // Tabs: 0 My Profile, 1 My Artworks, 2 Nearby Art, 3 Host Requests, 4 Upload, 5 My Favorites, 6 My Sales, 7 My Payouts
  const [tab, setTab] = useState(0);
  const [nearbySubTab, setNearbySubTab] = useState(0); // 0 hosts, 1 artists
  const [hostRequestsSubTab, setHostRequestsSubTab] = useState(0); // 0 pending, 1 accepted, 2 rejected, 3 expired, 4 cancelled, 5 sold
  const [salesRows, setSalesRows] = useState([]);
  const [salesSummary, setSalesSummary] = useState({});
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState("");
  const [salesYear, setSalesYear] = useState(String(new Date().getFullYear()));
  const [salesActionPaymentId, setSalesActionPaymentId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const {
    agreements,
    groupedAgreements,
    loading: agreementsLoading,
    refreshing: agreementsRefreshing,
    refreshAgreements,
    acceptAgreement,
    rejectAgreement,
    counterofferAgreement,
    requestAgreementCancellation,
    confirmAgreementCancellation,
    rejectAgreementCancellation,
    markAgreementSold,
  } = useArtistHostedArtworks({ enabled: Boolean(artist?.id) });
  const pendingAgreementsLoading = agreementsLoading || agreementsRefreshing;

  useEffect(() => {
    const fetchArtistData = async () => {
      try {
        const { data } = await http.get('/api/accounts/me/');
        const userId = data.id;

        const resArtist = await http.get(`/artists/?user=${userId}`);
        if (!resArtist.data.length) {
          console.warn('No artist profile found for this user.');
          alert('You need to complete your artist profile before accessing the dashboard.');
          navigate('/artist-profile/setup');
          return;
        }

        const artistProfile = resArtist.data[0];
        const fullProfile = {
          ...artistProfile,
          first_name: artistProfile.user.first_name,
          last_name: artistProfile.user.last_name,
          email: artistProfile.user.email,
        };
        setArtist(fullProfile);

        if (artistProfile?.location) {
          const coords = [artistProfile.location.latitude, artistProfile.location.longitude];
          setPosition(coords);
        }

        const resArts = await http.get(`/artworks/?artist=${artistProfile.id}&view=card`);
        setArtworks(resArts.data || []);

      } catch (error) {
        console.error('Error fetching artist data:', error);
      }
    };

    fetchArtistData();
  }, [navigate]);

  useEffect(() => {
    if (position) {
      console.log(`Map centered at: ${position[0]}, ${position[1]}`);
    }
  }, [position]);

  useEffect(() => {
    if (tab !== 6) return;
    let cancelled = false;
    async function loadArtistSales() {
      setSalesLoading(true);
      setSalesError("");
      try {
        const data = await getArtistSales(`year=${encodeURIComponent(salesYear)}`);
        if (!cancelled) {
          setSalesRows(data.results || []);
          setSalesSummary(data.summary || {});
        }
      } catch (error) {
        if (!cancelled) setSalesError(error.message || "Unable to load sales records.");
      } finally {
        if (!cancelled) setSalesLoading(false);
      }
    }
    loadArtistSales();
    return () => {
      cancelled = true;
    };
  }, [tab, salesYear]);

  useEffect(() => {
    if (!position) return;

    const fetchNearby = async () => {
      const [lat, lon] = position;

      try {
        const nh = await http.get(`/hosts/nearby/?lat=${lat}&lon=${lon}&radius=${distance}&view=card`);
        setNearbyHosts(nh.data || []);
      } catch (_) {
        setNearbyHosts([]);
      }

      try {
        const na = await http.get(`/artists/nearby/?lat=${lat}&lon=${lon}&radius=${distance}&view=card`);
        setNearbyArtists(na.data || []);
      } catch (_) {
        setNearbyArtists([]);
      }
    };

    fetchNearby();
  }, [distance, position]);

  const refreshArtistArtworks = async () => {
    if (!artist?.id) return;
    const res = await http.get(`/artworks/?artist=${artist.id}&view=card`);
    setArtworks(res.data || []);
  };

  const handleAgreementSelect = (agreement, mode = "respond") => {
    if (isPendingCancelStatus(agreement?.status)) {
      handleOpenCancelConfirmation(agreement);
      return;
    }
    setSelectedAgreement(agreement);
    setAgreementModalMode(mode);
    setAgreementModalOpen(true);
  };

  const handleRequestAgreementCancellation = async (agreement) => {
    if (!agreement?.id) return;
    try {
      await requestAgreementCancellation(agreement.id);
      await refreshAgreements();
      setSnackbar({ open: true, message: "Cancellation request sent.", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || "Failed to request cancellation.", severity: "error" });
    }
  };

  const isPendingCancelStatus = (status) => {
    const normalized = String(status || "").toUpperCase();
    return normalized === "PENDING_CANCEL_OK_HOST" || normalized === "PENDING_CANCEL_OK_ARTIST";
  };

  const handleOpenCancelConfirmation = (agreement) => {
    setCancelAgreement(agreement);
    setCancelConfirmOpen(true);
  };

  const handleCancelConfirmationClose = () => {
    if (cancelSubmitting) return;
    setCancelConfirmOpen(false);
    setCancelAgreement(null);
  };

  const handleConfirmAgreementCancel = async () => {
    if (!cancelAgreement?.id) return;
    try {
      setCancelSubmitting(true);
      await confirmAgreementCancellation(cancelAgreement.id);
      await refreshAgreements();
      await refreshArtistArtworks();
      setSnackbar({ open: true, message: "Agreement cancelled.", severity: "success" });
      setCancelConfirmOpen(false);
      setCancelAgreement(null);
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || "Failed to confirm cancellation.", severity: "error" });
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleRejectAgreementCancel = async () => {
    if (!cancelAgreement?.id) return;
    try {
      setCancelSubmitting(true);
      await rejectAgreementCancellation(cancelAgreement.id);
      await refreshAgreements();
      setSnackbar({ open: true, message: "Cancellation request rejected.", severity: "success" });
      setCancelConfirmOpen(false);
      setCancelAgreement(null);
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || "Failed to reject cancellation.", severity: "error" });
    } finally {
      setCancelSubmitting(false);
    }
  };


  const handleMarkSold = async (artworkId) => {
    if (!artworkId) return;
    try {
      const agreement = agreements.find((item) => Number(item?.artwork?.id) === Number(artworkId));
      let payload = null;
      if (agreement?.id) {
        payload = await markAgreementSold(agreement.id, { artworkId });
      }
      await refreshAgreements();
      await refreshArtistArtworks();
      const saleBreakdown = payload?.sale_breakdown || payload?.data?.sale_breakdown;
      const artistAmount = saleBreakdown?.artist_amount;
      const hostAmount = saleBreakdown?.host_amount;
      const breakdownMessage = (artistAmount !== undefined && hostAmount !== undefined)
        ? `Artwork marked as sold. Artist share: $${artistAmount}. Host share: $${hostAmount}.`
        : "Artwork marked as sold.";
      setSnackbar({ open: true, message: breakdownMessage, severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || "Failed to mark artwork as sold.", severity: "error" });
    }
  };

  const refreshSales = async () => {
    const data = await getArtistSales(`year=${encodeURIComponent(salesYear)}`);
    setSalesRows(data.results || []);
    setSalesSummary(data.summary || {});
  };

  const handleApproveRefund = async (row) => {
    const confirmed = window.confirm(`Approve the refund request for ${row?.artwork?.title || "this artwork"}? Stripe will begin processing immediately.`);
    if (!confirmed) return;
    setSalesActionPaymentId(row.payment_id);
    setSalesError("");
    try {
      await approvePaymentRefund(row.payment_id);
      await refreshSales();
      setSnackbar({ open: true, message: "Refund approved and sent to Stripe.", severity: "success" });
    } catch (error) {
      setSalesError(error.message || "Unable to approve the refund.");
    } finally {
      setSalesActionPaymentId(null);
    }
  };

  const handleRejectRefund = async (row) => {
    const rejectionReason = window.prompt(`Why are you rejecting the refund request for ${row?.artwork?.title || "this artwork"}?`);
    if (rejectionReason === null) return;
    if (!rejectionReason.trim()) {
      setSalesError("A rejection reason is required.");
      return;
    }
    const confirmed = window.confirm(`Reject the refund request for ${row?.artwork?.title || "this artwork"}?`);
    if (!confirmed) return;
    setSalesActionPaymentId(row.payment_id);
    setSalesError("");
    try {
      await rejectPaymentRefund(row.payment_id, rejectionReason);
      await refreshSales();
      setSnackbar({ open: true, message: "Refund request rejected.", severity: "success" });
    } catch (error) {
      setSalesError(error.message || "Unable to reject the refund.");
    } finally {
      setSalesActionPaymentId(null);
    }
  };

  const handleExportSalesCsv = async () => {
    try {
      const result = await exportArtistSalesCsv(`year=${encodeURIComponent(salesYear)}`);
      const csvText = typeof result === "string" ? result : (result?.raw || "");
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `artist-sales-${salesYear}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setSalesError(error.message || "Unable to export the sales report.");
    }
  };

  const handleAcceptAgreement = async (agreementId) => {
    await acceptAgreement(agreementId);
    await refreshAgreements();
    setSnackbar({ open: true, message: "Signed.", severity: "success" });
  };

  const handleRejectAgreement = async (agreementId) => {
    await rejectAgreement(agreementId);
    await refreshAgreements();
    setSnackbar({ open: true, message: "Request rejected.", severity: "success" });
  };

  const filteredArtworks = React.useMemo(() => {
    if (!Array.isArray(artworks)) return [];
    if (statusFilter.length === 0) return artworks; // filter OFF
    const set = new Set(statusFilter);
    return artworks.filter(a => set.has(a?.status));
  }, [artworks, statusFilter]);

  const goToHostProfile = (hostId) => navigate(`/hosts/${hostId}`);
  const goToArtistProfile = (artistId) => navigate(`/artists/${artistId}`);
  const getFullProfile = (profile) => {
        const fullProfile =  {
          ...profile,
          first_name: profile.user?.first_name,
          last_name: profile.user?.last_name,
          email: profile.user?.email,
        };  
        return(fullProfile);
      }

  const blurredTextSx = {
    display: 'inline-block',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(14px)',
    borderRadius: 1.5,
    px: 1.5,
    py: 0.75,
    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
    color: '#111827'
  };

  if (!artist) return <Typography sx={blurredTextSx}>Loading...</Typography>;

  return (
    <Container sx={{ mt: 5 }}>
      {/* Dashboard title */}
      <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom align="center" sx={blurredTextSx}>
          {artist.user.first_name} {artist.user.last_name}'s Dashboard
        </Typography>
      </Box>

      {/* Tabs right under title, styled */}
      <Box
        sx={{
          position: 'sticky',
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(14px)',
          px: 1,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <MuiTooltip title="Help for artist dashboard">
          <IconButton
            component={RouterLink}
            to="/help"
            state={{ backgroundLocation: location, helpSection: 'artist-help' }}
            size="small"
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.75)',
              backdropFilter: 'blur(14px)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
              color: '#111827',
              fontWeight: 800,
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.95)' },
            }}
          >
            ?
          </IconButton>
        </MuiTooltip>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTabs-flexContainer': { gap: 1 },
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 44,
              fontWeight: 600,
              //color: '#374151',
              borderRadius: 1.5,
              border: '1px solid transparent',
            },
            '& .Mui-selected': {
              backgroundColor: 'rgba(255, 255, 255, 0.4)',
            }
          }}
        >
          <Tab label="My Profile" />
          <Tab label="My Artworks" />
          <Tab label="Nearby Art" />
          <Tab label="Manage Hosted Artworks" />
          <Tab label="Upload New Artwork" />
          <Tab label="My Favorites" />
          <Tab label="My Sales" />
          <Tab label="My Payouts" />
        </Tabs>
      </Box>

      {/* My Profile */}
      {tab === 0 && (
        <Box sx={{ mt: 2 }}>
          <ArtistProfileCard user={artist} isSelf={true} />
        </Box>
      )}

      {/* My Artworks */}
      {tab === 1 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}>
            <FormControl
              size="small"
              sx={{
                minWidth: 260,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(14px)',
                  borderRadius: 2,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                }
              }}
            >
              <InputLabel id="artwork-status-filter-label">Filter by status</InputLabel>
              <Select
                labelId="artwork-status-filter-label"
                multiple
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                input={<OutlinedInput label="Filter by status" />}
                renderValue={(selected) =>
                  selected.length === 0
                    ? "All statuses"
                    : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => <Chip key={value} label={value} />)}
                      </Box>
                    )
                }
              >
                {ARTWORK_STATUSES.map((name) => (
                  <MenuItem key={name} value={name}>
                    <Checkbox checked={statusFilter.indexOf(name) > -1} />
                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Quick clear button when something is selected */}
            {statusFilter.length > 0 && (
              <Button variant="text" onClick={() => setStatusFilter([])}>
                Clear
              </Button>
            )}

            {filteredArtworks.length > 0 && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ...blurredTextSx, ml: 'auto' }}
              >
                ({filteredArtworks.length} shown)
              </Typography>
            )}
          </Box>

          <Grid container spacing={3}>
            {filteredArtworks.map((art) => (
              <Grid item xs={12} md={6} lg={4} key={art.id}>
                <ArtworkCard
                  art={art}
                  showActions={true}
                  onMarkSold={handleMarkSold}
                  allowMarkSold={false}
                  canManageOverride={true}
                />
              </Grid>
            ))}
          </Grid>

          {filteredArtworks.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ ...blurredTextSx, mt: 1 }}>
              No artworks match the selected status{statusFilter.length > 1 ? "es" : ""}.
            </Typography>
          )}
        </Box>
      )}

      {/* Nearby Art (map + sub-tabs for hosts/artists) */}
      {tab === 2 && (
        <Box sx={{ mt: 2 }}>
          {/* Radius control */}
          <Box width="100%" maxWidth="600px" mx="auto" my={2}>
            <Typography gutterBottom sx={blurredTextSx}>Search radius: {distance} km</Typography>
            <Slider
              value={distance}
              onChange={(e, newValue) => setDistance(newValue)}
              min={1}
              max={200}
              valueLabelDisplay="auto"
            />
          </Box>

          {/* Map */}
          {artist.location && position && (
            <MapContainer
              center={position}
              zoom={10}
              style={{ height: '400px', marginBottom: '12px' }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <RecenterOnPosition position={position} />
              <MapClickHandler onClick={setPosition} />

              {/* You (artist) */}
              <Marker
                position={[artist.location.latitude, artist.location.longitude]}
                icon={redIcon}
              >
                <Tooltip>{artist.user.first_name} {artist.user.last_name} (you)</Tooltip>
                <Popup>
                  <MiniArtistCard id={artist.id} />
                  <Stack direction="row" spacing={1} mt={1}>
                    <Button size="small" variant="outlined" onClick={() => goToArtistProfile(artist.id)}>
                      View Profile
                    </Button>
                  </Stack>
                </Popup>
              </Marker>
              <Circle center={position} radius={distance * 1000} pathOptions={{ color: 'blue' }} />

              {/* Hosts */}
              {nearbyHosts.map((host) => (
                <Marker
                  key={`h-${host.id}`}
                  position={[host.location.latitude, host.location.longitude]}
                  icon={blueIcon}
                >
                  <Tooltip>{host.display_name || `${host.user?.first_name || ''} ${host.user?.last_name || ''}`.trim()}</Tooltip>
                  <Popup>
                    <MiniHostCard id={host.id} />
                    <Stack direction="row" spacing={1} mt={1}>
                      <Button size="small" variant="outlined" onClick={() => goToHostProfile(host.id)}>
                        View Profile
                      </Button>
                      {isAuthenticated && handleStartChat && (
                        <Button size="small" variant="contained" onClick={() => handleStartChat(host)}>
                          Message
                        </Button>
                      )}
                    </Stack>
                  </Popup>
                </Marker>
              ))}

              {/* Artists */}
              {nearbyArtists.map((a) => (
                <Marker
                  key={`a-${a.id}`}
                  position={[a.location.latitude, a.location.longitude]}
                  icon={greenIcon}
                >
                  <Tooltip>{a.display_name || `${a.user?.first_name || ''} ${a.user?.last_name || ''}`.trim()}</Tooltip>
                  <Popup>
                    <MiniArtistCard id={a.id} />
                    <Stack direction="row" spacing={1} mt={1}>
                      <Button size="small" variant="outlined" onClick={() => goToArtistProfile(a.id)}>
                        View Profile
                      </Button>
                      {isAuthenticated && handleStartChat && (
                        <Button size="small" variant="contained" onClick={() => handleStartChat(a)}>
                          Message
                        </Button>
                      )}
                    </Stack>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}

          {/* Sub-tabs: Nearby Hosts / Nearby Artists */}
          <Box
            sx={{
              borderRadius: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(14px)',
              px: 1,
              py: 0.5,
              mb: 1
            }}
          >
            <Tabs
              value={nearbySubTab}
              onChange={(e, v) => setNearbySubTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { textTransform: 'none', minHeight: 40, borderRadius: 1.5, border: '1px solid transparent', fontWeight: 600 },
                '& .Mui-selected': { backgroundColor: 'rgba(255, 255, 255, 0.4)'}
              }}
            >
              <Tab label="Nearby Hosts" />
              <Tab label="Nearby Artists" />
            </Tabs>
          </Box>

          {/* Lists under sub-tabs */}
          {nearbySubTab === 0 && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                {nearbyHosts.map((h) => (
                  <Grid item xs={12} md={6} lg={4} key={h.id}>
                    <HostProfileCard user={getFullProfile(h)} onStartChat={handleStartChat} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {nearbySubTab === 1 && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                {nearbyArtists.map((a) => (
                  <Grid item xs={12} md={6} lg={4} key={a.id}>
                    <ArtistProfileCard user={getFullProfile(a)} isSelf={false} onStartChat={handleStartChat} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      )}

      {/* Host Requests */}
      {tab === 3 && (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              borderRadius: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(14px)',
              px: 1,
              py: 0.5,
              mb: 1
            }}
          >
            <Tabs
              value={hostRequestsSubTab}
              onChange={(e, v) => setHostRequestsSubTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { textTransform: 'none', minHeight: 40, borderRadius: 1.5, border: '1px solid transparent', fontWeight: 600 },
                '& .Mui-selected': { backgroundColor: 'rgba(255, 255, 255, 0.4)'}
              }}
            >
              <Tab label="Pending" />
              <Tab label="Accepted" />
              <Tab label="Rejected" />
              <Tab label="Expired" />
              <Tab label="Cancelled" />
              <Tab label="Sold" />
            </Tabs>
          </Box>
          {pendingAgreementsLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading host requests...</Typography>
            </Box>
          )}
          {!pendingAgreementsLoading && hostRequestsSubTab === 0 && (
            <PendingAgreementsList
              agreements={groupedAgreements.pending}
              onSelect={handleAgreementSelect}
              roleLabel="artist"
              emptyLabel="No pending host requests right now."
              renderActions={(agreement) => (
                isPendingCancelStatus(agreement?.status)
                  ? [{
                    key: "review-cancel-request",
                    label: "Review Cancel Request",
                    onClick: () => handleOpenCancelConfirmation(agreement),
                  }]
                  : []
              )}
            />
          )}
          {!pendingAgreementsLoading && hostRequestsSubTab === 1 && (
            <PendingAgreementsList
              agreements={groupedAgreements.accepted}
              roleLabel="artist"
              emptyLabel="No accepted host requests yet."
              renderActions={(agreement) => ([
                {
                  key: "change-terms",
                  label: "Change Agreement Terms",
                  onClick: () => handleAgreementSelect(agreement, "changeTerms"),
                },
                {
                  key: "request-cancel",
                  label: "Request to Cancel Agreement",
                  onClick: () => handleRequestAgreementCancellation(agreement),
                },
              ])}
            />
          )}
          {!pendingAgreementsLoading && hostRequestsSubTab === 2 && (
            <PendingAgreementsList
              agreements={groupedAgreements.rejected}
              roleLabel="artist"
              emptyLabel="No rejected host requests."
            />
          )}
          {!pendingAgreementsLoading && hostRequestsSubTab === 3 && (
            <PendingAgreementsList
              agreements={groupedAgreements.expired}
              roleLabel="artist"
              emptyLabel="No expired host requests."
            />
          )}
          {!pendingAgreementsLoading && hostRequestsSubTab === 4 && (
            <PendingAgreementsList
              agreements={groupedAgreements.cancelled}
              roleLabel="artist"
              emptyLabel="No cancelled host requests."
            />
          )}
          {!pendingAgreementsLoading && hostRequestsSubTab === 5 && (
            <PendingAgreementsList
              agreements={groupedAgreements.sold}
              roleLabel="artist"
              emptyLabel="No sold hosting agreements."
            />
          )}
        </Box>
      )}

      {/* Upload New Artwork — uses existing page */}
      {tab === 4 && (
        <Box sx={{ mt: 2 }}>
          <ArtworkCreatePage
            // If your component supports it, this will refresh the list and switch to “My Artworks”
            onCreated={(newArtwork) => {
              if (newArtwork) setArtworks((prev) => [newArtwork, ...prev]);
              setTab(1);
            }}
            // If you support inline rendering toggle or breadcrumbs, you can pass props here
            inline
          />
        </Box>
      )}

      {/* My Favorites */}
      {tab === 5 && (
        <Box sx={{ mt: 2, gap: 2, display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
          <FavoritesSection type="artworks" maxWidth={1024}/>
          <FavoritesSection type="artists" maxWidth={1024}/>
          <FavoritesSection type="hosts" maxWidth={1024}/>
        </Box>
      )}

      {tab === 6 && (
        <Box sx={{ mt: 2 }}>
          <SalesReportPanel
            title="Artist Sales"
            roleLabel="Artist"
            rows={salesRows}
            summary={salesSummary}
            loading={salesLoading}
            error={salesError}
            year={salesYear}
            onYearChange={setSalesYear}
            onExportCsv={handleExportSalesCsv}
            onApproveRefund={handleApproveRefund}
            onRejectRefund={handleRejectRefund}
            actingPaymentId={salesActionPaymentId}
          />
        </Box>
      )}

      {/* My Payouts (last) */}
      {tab === 7 && (
        <Box sx={{ mt: 2 }}>
          <PayoutsCard roleLabel="Artist" />
        </Box>
      )}

      <ArtworkAgreementEditorModal
        open={agreementModalOpen}
        onClose={() => setAgreementModalOpen(false)}
        agreement={selectedAgreement}
        mode={agreementModalMode}
        onAccept={handleAcceptAgreement}
        onReject={handleRejectAgreement}
        onCounteroffer={counterofferAgreement}
      />

      <Dialog open={cancelConfirmOpen} onClose={handleCancelConfirmationClose}>
        <DialogTitle>Cancellation Confirmation Requested</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The other party requested to cancel this accepted agreement. Accept cancellation to move this agreement to CANCELLED, or reject to keep it ACCEPTED.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirmationClose} disabled={cancelSubmitting}>Close</Button>
          <Button variant="outlined" onClick={handleRejectAgreementCancel} disabled={cancelSubmitting}>Reject Cancellation</Button>
          <Button variant="contained" color="error" onClick={handleConfirmAgreementCancel} disabled={cancelSubmitting}>Accept Cancellation</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Container>
  );
}
